import Box from '@mui/material/Box';
import { TextField } from '@openx/components-core/lib/TextField/TextField';
import { type ListSegment, renameSegmentFormField } from '@openx/types/segments';
import { type ChangeEvent, type ForwardedRef, forwardRef, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  segment: ListSegment;
  setConfirmationDisabled: (disabled: boolean) => void;
};

export const RenameSegmentDialogContent = forwardRef(
  ({ segment, setConfirmationDisabled }: Props, textFieldRef: ForwardedRef<HTMLInputElement>) => {
    const { t } = useTranslation();

    const [name, setName] = useState(segment.name);
    const [errorText, setErrorText] = useState<string | undefined>();

    const onChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const name = event.target.value;
        setName(name);
        setErrorText(undefined);

        if (!name) {
          setErrorText(t('Name cannot be empty'));
        }

        if (name === segment.name) {
          setErrorText(t('New name has to be different than current'));
        }
      },
      [t, segment.name]
    );

    useEffect(() => {
      setConfirmationDisabled(!!errorText || name === segment.name);
    }, [setConfirmationDisabled, errorText, name, segment.name]);

    return (
      <Box
        data-test="confirmation-dialog-content"
        sx={{
          padding: theme => theme.spacing(3, 2.5),
        }}
      >
        <TextField
          data-test="new-name-input"
          inputRef={textFieldRef}
          error={!!errorText}
          helperText={errorText}
          name={renameSegmentFormField}
          label={t('New name')}
          value={name}
          onChange={onChange}
          fullWidth
        />
      </Box>
    );
  }
);
